import {
  Button,
  CircularProgress,
  Divider,
  Typography
} from '@material-ui/core'
import LockIcon from '@material-ui/icons/Lock'
import Alert from '@material-ui/lab/Alert'
import React from 'react'

import VisuallyHidden from '../../components/VisuallyHidden'
import { TICKET_COMMENTS_QUERY } from '../../graphql/tickets'
import { validateCommentsResponse } from '../../helpers/ticketResponseValidation'
import {
  commentDisplay,
  shortIdentifier,
  ticketCommentScopeLabel,
  TicketComment,
  TicketCommentList
} from '../../helpers/ticketWorkflow'
import { useCursorPage } from '../../helpers/useCursorPage'
import { getTranslate } from '../../localization'
import SectionCard from './SectionCard'
import When from './When'
import useStyles from './styles/index.styles'

/**
 * A withheld comment. Rendered as a placeholder rather than omitted so the
 * thread keeps its shape — the reader can see that a private exchange happened
 * here, which is information they could already derive from the ticket's
 * visible reporter and assignee fields.
 */
function WithheldComment({
  comment
}: {
  // Deliberately narrowed to the ONE field a placeholder needs. The full
  // comment object still carries body and authorUserId (empty, per the server
  // contract) and a wider type would let a future "show who wrote it" edit
  // destructure them without the compiler objecting.
  comment: Pick<TicketComment, 'createdAt'>
}) {
  return (
    <div style={{ padding: '8px 0', opacity: 0.7 }}>
      <Typography variant="body2" color="textSecondary">
        <LockIcon
          fontSize="inherit"
          style={{ verticalAlign: 'middle', marginInlineEnd: 4 }}
        />
        {getTranslate('Private note between the reporter and the assignee.')}{' '}
        <When iso={comment.createdAt} absolute />
      </Typography>
    </div>
  )
}

function VisibleComment({
  comment,
  isDescription = false
}: {
  comment: TicketComment
  /**
   * This comment is the ticket's original problem statement.
   *
   * Marked rather than moved. The description IS a comment — CreateTicket
   * writes `body` as the first PARTICIPANT_PUBLIC one — so lifting it out of
   * the thread would either duplicate it or leave a hole where the
   * conversation starts. Labelling it in place keeps one chronology.
   */
  isDescription?: boolean
}) {
  const classes = useStyles()
  const internal = comment.scope === 'ADOTEL_INTERNAL'
  return (
    <div
      style={{ padding: '8px 0' }}
      className={internal ? classes.commentInternal : undefined}>
      <Typography variant="body2" color="textSecondary">
        {/* Leads the metadata line, ahead of the scope, because it answers a
            different question: the scope says who may read this, the label says
            what it IS. A reader scanning a long thread for the original report
            should not have to reach the timestamp to find it. */}
        {isDescription && (
          <>
            <strong>{getTranslate('Original description')}</strong> ·{' '}
          </>
        )}
        {/* The scope leads, and for an internal note it carries the lock this
            file already uses for the withheld placeholder. It was the third
            item of a grey run-on — the same weight as the author id and the
            timestamp, which is the weight of metadata rather than of the fact
            that decides whether the customer can read this. */}
        {internal && (
          <LockIcon
            fontSize="inherit"
            aria-hidden="true"
            style={{ verticalAlign: 'middle', marginInlineEnd: 4 }}
          />
        )}
        <strong>{ticketCommentScopeLabel(comment.scope)}</strong> ·{' '}
        {/* The author as a person when the BFF could resolve them, the
            truncated id when it could not.

            bdi WITHOUT dir="ltr" for a resolved name: a name may be Persian,
            and forcing LTR reverses it. The id keeps dir="ltr" because it is a
            Latin machine string — the same distinction the SLA panel draws
            between its dates and its policy id. */}
        {comment.authorDisplayName ? (
          <bdi>{comment.authorDisplayName}</bdi>
        ) : (
          <bdi dir="ltr">{shortIdentifier(comment.authorUserId)}</bdi>
        )}{' '}
        · <When iso={comment.createdAt} absolute />
      </Typography>
      {/* Rendered as text, never as HTML. Bodies are free text written by
          users including cross-organization participants; React escapes text
          children, and switching this to dangerouslySetInnerHTML would make
          every comment an injection vector.

          dir="auto" because this is the one string on the screen whose
          direction cannot be known in advance. Everything else here is either
          an identifier pinned LTR or a localized date left to inherit; a
          comment body is operator- or customer-written prose that may be
          Persian on an English page or English on a Persian one. Inheriting
          the document direction puts the trailing punctuation of a Persian
          sentence on the wrong edge. */}
      <Typography variant="body1" dir="auto" style={{ whiteSpace: 'pre-wrap' }}>
        {comment.body}
      </Typography>
    </div>
  )
}

/**
 * The ticket conversation.
 *
 * Paging is driven ENTIRELY by `nextPageToken`, via `useCursorPage`. The count
 * of returned comments carries no information about whether more exist: the
 * server filters a page after reading it, so a full page of stored comments
 * can arrive short or empty with more behind it. That rule, and the hand-merge
 * this app needs because its InMemoryCache declares no typePolicies, live in
 * the hook rather than here — three lists depended on getting both right.
 */
export default function CommentThread({
  ticketId,
  children
}: {
  ticketId: string
  /**
   * Rendered inside the thread's own Paper, after the messages.
   *
   * The composer used to be a separate Paper BELOW this one, so the reply box
   * and the message it answers were never on the same surface — and on a long
   * ticket, never on screen together. Taken as children rather than built in
   * here: the mutation, its scope and its error state belong to the screen that
   * owns them, and moving them in would drag that state across the boundary.
   */
  children?: React.ReactNode
}) {
  const {
    items: comments,
    loading,
    error,
    validationError,
    hasMore,
    loadMore
  } = useCursorPage<{ ticketComments: TicketCommentList }, TicketComment>({
    query: TICKET_COMMENTS_QUERY,
    variables: { ticketId },
    // No maxRetained: a thread is bounded by one ticket's conversation, unlike
    // the cross-tenant queue.
    getPage: (data) =>
      data && {
        items: data.ticketComments.comments,
        nextPageToken: data.ticketComments.nextPageToken
      },
    withItems: (data, items) => ({
      ticketComments: { ...data.ticketComments, comments: items }
    }),
    validate: validateCommentsResponse
  })

  // A background refetch can fail while cached comments are still on screen
  // (cache-and-network). Replacing the whole thread with a banner would throw
  // away a conversation the user was reading because a later poll failed, so
  // the error is shown ALONGSIDE whatever is already loaded and only replaces
  // the thread when there is nothing to show.
  const errorBanner =
    error || validationError ? (
      <Alert severity="error" style={{ marginBottom: 8 }}>
        {/* Only a GraphQL error has been through the BFF's status-code
          allowlist. A network or proxy failure never reaches that mapping, so
          its message is not covered by that guarantee and is replaced here. */}
        {(validationError && getTranslate(validationError)) ||
          (error?.graphQLErrors?.length
            ? error.message
            : getTranslate('Could not load the conversation.'))}
      </Alert>
    ) : null

  // Raised, not quiet. This is the work; the reference column is what gets
  // demoted around it. The id names the conversation COLUMN's landmark in the
  // detail screen, so the region and its heading say the same thing rather than
  // duplicating a string. h2 because the two columns are the page's top-level
  // structure under the ticket title.
  return (
    <SectionCard
      heading={getTranslate('Conversation')}
      level="h2"
      headingId="ticket-conversation">
      <Divider style={{ margin: '8px 0' }} />

      {errorBanner}

      {comments.map((comment, index) => (
        <div key={comment.id}>
          {commentDisplay(comment) === 'withheld' ? (
            <WithheldComment comment={comment} />
          ) : (
            /* The oldest comment IS the ticket's description — CreateTicket
               writes `body` as the first PARTICIPANT_PUBLIC comment rather than
               storing it on the ticket. Nothing said so, so a reader saw the
               problem statement styled exactly like a reply and reasonably
               concluded the ticket had no description at all.

               index === 0 is safe here, and only because the ordering is
               server-guaranteed: ticket-service sorts `created_at ASC, id ASC`
               and pages forward with a `>` cursor, so page one starts at the
               oldest comment and later pages only ever append newer ones. It
               would NOT be safe under newest-first paging. */
            <VisibleComment comment={comment} isDescription={index === 0} />
          )}
        </div>
      ))}

      {loading && (
        <div style={{ padding: 8, textAlign: 'center' }}>
          <CircularProgress size={20} />
          <VisuallyHidden live="polite">
            {getTranslate('Loading')}
          </VisuallyHidden>
        </div>
      )}

      {/* An empty thread and a loading one are different states. Note this is
          only shown once loading has settled AND no further page is pending —
          an empty first page with a token is not an empty conversation. */}
      {!loading &&
        !error &&
        !validationError &&
        !comments.length &&
        !hasMore && (
          <Typography variant="body2" color="textSecondary">
            {getTranslate('No comments yet.')}
          </Typography>
        )}

      {hasMore && (
        <Button
          size="small"
          variant="outlined"
          disabled={loading}
          style={{ marginTop: 8 }}
          onClick={loadMore}>
          {/* "earlier" was backwards. The server sorts created_at ASC and
              pages with a `>` cursor, so page one holds the OLDEST comments
              and this button fetches newer ones. A reader on a long ticket
              pressed it expecting history and got the opposite. */}
          {getTranslate('Load more comments')}
        </Button>
      )}

      {children}
    </SectionCard>
  )
}
